import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useState, } from "react"
import { handleError, userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import TabsList from "../../../components/Common/TabsList"
import { Accordion, Box, Button, Container, createListCollection, Flex, HStack, Icon, Select, Text } from "@chakra-ui/react"
import { PrioCategoryBlock, readCategories, readSettingsPrioCategories, sortBlocks } from "../../../api/categories"
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core"
import { arrayMove, SortableContext, verticalListSortingStrategy, } from "@dnd-kit/sortable"
import { SortableItem } from "../../../components/Common/SortableItem"
import { FiMenu, FiPlusCircle, FiTrash2 } from "react-icons/fi"
import { Tooltip } from "../../../components/UI/tooltip"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { SubmitHandler, useForm } from "react-hook-form"
import { ApiError, SettingsPriocategoriesService, SettingsPrioCategoriesUpdate } from "../../../client"
import useCustomToast from "../../../hooks/useCustomToast"
import { FiInfo } from "react-icons/fi"

export const Route = createFileRoute("/_layout/settings/priocategories")({
  component: Priocategories,
})


function PriocategoriesTable() {
    // Permission check
    const [pc_read, pc_update, ct_read] = useMemo(() => {
        const [pc_read, pc_update] = ["read", "update"].map((action) => userHasPermission([{ perm: `settings_priocategories_${action}`, is_allowed: false }])[0].is_allowed)
        const [ct_read] =  ["read"].map((action) => userHasPermission([{ perm: `categories_${action}`, is_allowed: false }])[0].is_allowed)

        return [pc_read, pc_update, ct_read]
    }, [userHasPermission])

    if(!pc_read) return <NoPermission/>

    // Blocks
    const [blocks, setBlocks] = useState<PrioCategoryBlock[]>([])
    // Handle open items
    const [openItems, setOpenItems] = useState<string[]>([])

    // Get all categories
    const categoriesQuery = useMemo(() => readCategories({per_page: 0, page: 0, showInactive: false}), [])
    const { data: categoriesList } = useQuery(categoriesQuery)

    // Create category collection from all categories
    const categoryCollection = createListCollection({
        items: categoriesList?.data?.map(c => ({
            value: c.id,
            label: c.name,
        }))|| [],
    })

    // Functions for blocks
    const addBlock = () => {
        const newId = crypto.randomUUID()
        setBlocks(prev => [ ...prev, {
            id: newId,
            fk_category_id: null,
            prio: prev.length,
        }])

        // Set open state
        setOpenItems(prev => [...prev, newId])
    }

    const removeBlock = (id: string) => {
        setBlocks(prev => {
            const filtered = prev.filter(b => b.id !== id)

            // Reassign sort values
            return filtered.map((b, index) => ({
                ...b,
                prio: index,
            }))
        })
    }

    const updateCategory = (id: string, categoryId: string) => {
        setBlocks(prev => prev.map(b => b.id === id ? { ...b, fk_category_id: categoryId } : b ) )
    }

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        setBlocks(prev => {
            const oldIndex = prev.findIndex(b => b.id === active.id)
            const newIndex = prev.findIndex(b => b.id === over.id)

            const reordered = arrayMove(prev, oldIndex, newIndex)

            return reordered.map((b, i) => ({ ...b, prio: i }))
        })
    }

    // Query
    const queryClient = useQueryClient()
    const showToast = useCustomToast()

    const {data: priocategories} = useQuery({
        ...readSettingsPrioCategories(),
    })

    //Form default values
    const defaultValues = useMemo(() => ({
        prio_categories: priocategories?.data ?? [],
    }), [priocategories])

    // Form
    const isDirty = useMemo(() => {
        const initial = (priocategories?.data ?? [])
            .sort((a, b) => a.prio - b.prio)
            .map(item => ({
                id: item.id,
                fk_category_id: item.fk_category?.id ?? null,
                prio: item.prio,
            }))

        return JSON.stringify(initial) !== JSON.stringify(blocks)
    }, [priocategories, blocks])

    const isValid = useMemo(() => {
        return blocks.every(b => b.fk_category_id !== null)
    }, [blocks])

    const {
        handleSubmit,
        reset,
        formState: {isSubmitting},
    } = useForm<SettingsPrioCategoriesUpdate>({
        mode: "onBlur",
        criteriaMode: "all",
        defaultValues: defaultValues,
    })

    useEffect(() => {
        if (!priocategories?.data) return

        reset(defaultValues)

        // Set previous data
        if (priocategories?.data) {
            const sorted = sortBlocks(priocategories)
            setBlocks(sorted)
        }
    }, [priocategories?.data])

    // Mutate form data
    const mutation = useMutation({
        mutationFn: (data: SettingsPrioCategoriesUpdate) =>
            SettingsPriocategoriesService.updateSettingsPriocategories({ requestBody: data }),
        onSuccess: () => {
            showToast("Aktion erfolgreich!", "Kategorien wurden angepasst", "success")
            reset()
        },
        onError: (err: ApiError) => {
            handleError(err, showToast)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["settings-priocategories"] })
        },
    })

    // Submit form
    const onSubmit: SubmitHandler<SettingsPrioCategoriesUpdate> = async () => {
        const payload = {
            prio_categories: blocks.map(b => ({
                fk_category_id: b.fk_category_id!,
                prio: b.prio,
             }))
        }

        mutation.mutate(payload)
    }

    // Cancle form
    const onCancel = () => {
        reset()

        if (priocategories?.data) {
            const sorted = sortBlocks(priocategories)
            setBlocks(sorted)
        } else {
            setBlocks([])
        }
    }

    return (
        <Container maxW="full" my={10}>
            <HStack pb={4}>
                <Text>Priorisierte Kategorien</Text>
                <Tooltip
                    showArrow
                    content="Liste von Kagegorien, welche in Dropdown-Auswahl zu oberst angezeigt werden (Nur Redaktionssystem)"
                    openDelay={500}
                >
                    <Icon as={FiInfo} data-testid="info_priocategory" />
                </Tooltip>
            </HStack>

            <Box
                w={{ sm: "full", md: "50%" }}
                as="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <Box border="1px solid" borderColor="dim" borderRadius={8}>
                    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd} onDragStart={() => setOpenItems([])}>
                        <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                            <Accordion.Root multiple={true} value={openItems} onValueChange={(details) => setOpenItems(details.value)}>
                                {blocks.map(block => (
                                    <SortableItem id={block.id} key={block.id}>
                                        {({ listeners }) => (
                                            <Accordion.Item
                                                key={block.id}
                                                value={block.id}
                                                px={2}
                                                py={2}
                                                divideY="1px"
                                                divideColor="main"
                                            >
                                                <Accordion.ItemTrigger h="2.5em">
                                                    <Icon as={FiMenu} {...listeners} data-testid="drag_priocategory" cursor="pointer" _active={{cursor: "grabbing"}} />

                                                    <Box flex="1" textAlign="left" color={!block.fk_category_id ? "danger" : undefined} data-testid="title_priocategory">
                                                        {block.fk_category_id
                                                            ? categoriesList?.data?.find(c => c.id === block.fk_category_id)?.name
                                                            : "Neue Kategorie"
                                                        }

                                                        <Accordion.ItemIndicator display="none" />
                                                    </Box>

                                                    <Icon
                                                        as={FiTrash2}
                                                        color="danger"
                                                        onClick={(e) => { e.stopPropagation(), removeBlock(block.id) }}
                                                        data-testid="delete_priocategory"
                                                    />
                                                </Accordion.ItemTrigger>

                                                <Accordion.ItemContent pt={4} borderRadius="none">
                                                    <Accordion.ItemBody>
                                                        <Select.Root
                                                            multiple={false}
                                                            collection={categoryCollection}
                                                            value={[block.fk_category_id ?? ""]}
                                                            onValueChange={(value) => updateCategory(block.id, value.value[0])}
                                                        >
                                                            <Select.HiddenSelect />

                                                            <Select.Control>
                                                                <Select.Trigger>
                                                                    <Select.ValueText placeholder="Kategorie" />
                                                                </Select.Trigger>

                                                                <Select.IndicatorGroup>
                                                                    <Select.Indicator />
                                                                </Select.IndicatorGroup>
                                                            </Select.Control>

                                                            <Select.Positioner>
                                                                <Select.Content>
                                                                    {ct_read ? (
                                                                        categoriesList?.data ? (
                                                                            categoriesList?.data?.map(c => (
                                                                                <Select.Item item={c.id} key={c.id}>
                                                                                    {c.name}
                                                                                    <Select.ItemIndicator />
                                                                                </Select.Item>
                                                                            ))
                                                                        ) : (
                                                                            <Text>Keine Kategorien vorhanden</Text>
                                                                        )
                                                                    ) : (
                                                                        <Text color="danger">Keine Berechtigung für Kategorien</Text>
                                                                    )}
                                                                </Select.Content>
                                                            </Select.Positioner>
                                                        </Select.Root>

                                                        {block.fk_category_id === null &&
                                                            <Text color="danger" fontSize="sm" mt={1}> Kategorie ist erforderlich </Text>
                                                        }
                                                    </Accordion.ItemBody>
                                                </Accordion.ItemContent>
                                            </Accordion.Item>
                                        )}
                                    </SortableItem>
                                ))}
                            </Accordion.Root>
                        </SortableContext>
                    </DndContext>

                    <Tooltip showArrow content="Block hinzufügen" openDelay={500}>
                        <Button
                            onClick={addBlock}
                            w="100%"
                            h="fit-content"
                            py={2}
                            backgroundColor="transparent"
                            color="main"
                            data-testid="add_priocategory"
                        >
                            <Icon as={FiPlusCircle} boxSize="25px" />
                        </Button>
                    </Tooltip>
                </Box>

                <Flex mt={4} mb={8} gap={8}>
                    <Button
                        variant="primary"
                        type="submit"
                        loading={isSubmitting}
                        disabled={!isDirty || !pc_update || !isValid}
                    >
                        Speichern
                    </Button>

                    <Button
                        onClick={onCancel}
                        disabled={isSubmitting || !isDirty}
                        variant="secondary"
                    >
                        Abbrechen
                    </Button>
                </Flex>
            </Box>
        </Container>
    )
}

export default PriocategoriesTable

function Priocategories() {
    const tabs = useMemo(() => [
        { value: "settings", title: "Allgemein" },
        { value: "settings/navigation", title: "Navigation" },
        { value: "settings/landingpage", title: "Startseite" },
        { value: "settings/mimetypes", title: "Dateitypen" },
        { value: "settings/subscriptions", title: "Abos" },
        { value: "settings/priocategories", title: "Kategorien" },
        { value: "settings/ratecharts", title: "Tariftabellen" },
        { value: "settings/actions", title: "Aktionen" },
    ], [])

    return <TabsList tabs={tabs} defaultVal="settings" />
}
